/**
 * HERO mock: Postkorb (scan inbox) — Aktiv/Archiv, Scanner-Callout, Importieren.
 */
"use client";

import { ld } from "@/lib/theme/light-dark";
import { FileStack, Plus, ScanSearch } from "lucide-react";
import { type ReactNode, useState } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import { PostkorbPageGroup } from "./module-dokumente-postkorb-parts";

export type DokumentePostkorbListMode = "active" | "archived";

const POSTKORB_LIST_WIDTH = 196;

function PostkorbScannerCallout(): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePostkorb");
  return (
    <div
      style={{
        position: "absolute",
        right: 18,
        bottom: 72,
        zIndex: 24,
        width: 296,
        background: ld("#fff", "#181b1f"),
        border: `1px solid ${ld("#E6E8EC", "#33363b")}`,
        borderRadius: 15,
        boxShadow:
          `0 36px 72px -22px ${ld("rgba(11,15,25,0.46)", "rgba(0,0,0,0.74)")}, 0 12px 26px -12px ${ld("rgba(11,15,25,0.26)", "rgba(0,0,0,0.44)")}`,
        padding: "15px 14px 13px",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          style={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 8,
            background: H.accentSoft,
            color: H.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ScanSearch width={16} height={16} strokeWidth={1.9} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: H.ink,
              lineHeight: 1.35,
              whiteSpace: "nowrap",
            }}
          >
            {t("muellerScannerHausverwalterAi")}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 11.5,
              color: ld("rgba(11,15,25,0.56)", "rgba(250,250,250,0.56)"),
              lineHeight: 1.35,
            }}
          >
            {t("k2DokumenteEingescannt")}
          </div>
        </div>
      </div>
    </div>
  );
}

function PostkorbListToggle(props: {
  readonly mode: DokumentePostkorbListMode;
  readonly onChange: (mode: DokumentePostkorbListMode) => void;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePostkorb");
  return (
    <div
      style={{
        display: "inline-flex",
        alignSelf: "flex-start",
        padding: 3,
        borderRadius: 9,
        background: ld("#F1F2F4", "#2b2e32"),
        gap: 2,
      }}
    >
      {(
        [
          { key: "active" as const, label: "Aktiv" },
          { key: "archived" as const, label: t("archiv") },
        ] as const
      ).map((option) => {
        const active = props.mode === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => {
              props.onChange(option.key);
            }}
            style={{
              appearance: "none",
              border: "none",
              fontFamily: H.sans,
              padding: "5px 12px",
              borderRadius: 7,
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              cursor: "pointer",
              color: active ? H.ink : H.inkDim,
              background: active ? ld("#fff", "#181b1f") : "transparent",
              boxShadow: active ? `0 1px 2px ${ld("rgba(11,15,25,0.06)", "rgba(0,0,0,0.14)")}` : "none",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function PostkorbListColumn(props: {
  readonly mode: DokumentePostkorbListMode;
  readonly onModeChange: (mode: DokumentePostkorbListMode) => void;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePostkorb");
  return (
    <aside
      style={{
        width: POSTKORB_LIST_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#F7F8F9", "#222529"),
        padding: "14px 12px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: MODULE_HEIGHT,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 2px",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: H.ink }}>
          {t("postkorb")}
        </span>
        <span style={{ color: H.inkDim, display: "inline-flex", padding: 4 }}>
          <Plus width={15} height={15} strokeWidth={2} />
        </span>
      </div>

      <PostkorbListToggle mode={props.mode} onChange={props.onModeChange} />

      {props.mode === "active" ? (
        <button
          type="button"
          style={{
            appearance: "none",
            border: "none",
            fontFamily: H.sans,
            textAlign: "left",
            padding: "10px 10px",
            borderRadius: 9,
            background: H.accentSoft,
            cursor: "default",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: H.accent }}>
            {t("aktuellerPostkorb")}
          </div>
          <div style={{ fontSize: 11, color: H.inkDim, marginTop: 3 }}>
            {t("seit22052025")}
          </div>
        </button>
      ) : (
        <div
          style={{
            padding: "18px 8px",
            fontSize: 12,
            color: H.inkGhost,
            textAlign: "center",
          }}
        >
          {t("keineArchiviertenPostkoerbe")}
        </div>
      )}
    </aside>
  );
}

function PostkorbEditorColumn(props: {
  readonly listMode: DokumentePostkorbListMode;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePostkorb");
  const [viewMode, setViewMode] = useState<"current" | "original">("current");
  const isArchived = props.listMode === "archived";

  return (
    <section
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        height: MODULE_HEIGHT,
        background: ld("#fff", "#181b1f"),
      }}
    >
      <header
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 18px 12px",
          borderBottom: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: H.ink }}>
            {t("aktuellerPostkorb")}
          </div>
          <div style={{ fontSize: 11.5, color: H.inkDim, marginTop: 3 }}>
            {t("seit220520252")}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              padding: 2,
              borderRadius: 8,
              border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
              overflow: "hidden",
            }}
          >
            {(
              [
                { key: "current" as const, label: t("aktuell") },
                { key: "original" as const, label: "Original" },
              ] as const
            ).map((tab) => {
              const active = viewMode === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setViewMode(tab.key);
                  }}
                  style={{
                    appearance: "none",
                    border: "none",
                    fontFamily: H.sans,
                    padding: "4px 9px",
                    fontSize: 11,
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                    color: active ? H.accent : H.inkDim,
                    background: active ? H.accentSoft : "transparent",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: H.inkDim,
            }}
          >
            <FileStack width={13} height={13} strokeWidth={1.8} />
            {t("k2Dateien")}
          </span>
        </div>
      </header>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "16px 18px 12px",
        }}
      >
        {isArchived ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              color: H.inkGhost,
            }}
          >
            {t("waehlePostkorbArchiv")}
          </div>
        ) : (
          <>
            <PostkorbPageGroup
              label={t("dokument13Seiten")}
              pageCount={3}
              showCutAfterIndices={[1]}
            />
            <PostkorbPageGroup
              label={t("dokument21Seite")}
              pageCount={1}
              showCutAfterIndices={[]}
            />
          </>
        )}
      </div>

      {!isArchived ? (
        <footer
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 18px",
            borderTop: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
            background: ld("rgba(255,255,255,0.96)", "rgba(26,28,31,0.96)"),
          }}
        >
          <span style={{ fontSize: 12, color: H.inkDim }}>
            {t("k4Seiten2Dokumente")}
          </span>
          <button
            type="button"
            style={{
              appearance: "none",
              border: "none",
              fontFamily: H.sans,
              padding: "8px 16px",
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 600,
              color: ld("#fff", "#181b1f"),
              background: H.accent,
              cursor: "default",
              boxShadow: `0 2px 8px -2px ${ld("rgba(0,128,112,0.45)", "rgba(0,0,0,0.725)")}`,
            }}
          >
            {t("importieren")}
          </button>
        </footer>
      ) : null}
    </section>
  );
}

export function DokumentePostkorbView(): ReactNode {
  const [listMode, setListMode] = useState<DokumentePostkorbListMode>("active");

  return (
    <div
      style={{ position: "relative", display: "flex", flex: 1, minWidth: 0 }}
    >
      <PostkorbListColumn mode={listMode} onModeChange={setListMode} />
      <PostkorbEditorColumn listMode={listMode} />
      {listMode === "active" ? <PostkorbScannerCallout /> : null}
    </div>
  );
}
